// App.js
import React, { useEffect, useState, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/Login';
import MapComponent from './components/MapComponent';
import CsvUploader from './components/CsvUploader';
import StagingInterface from './components/StagingInterface';
import StagingInterfacePowerplant from './components/StagingInterfacePowerplants';

function App() {
  const [accessToken, setAccessToken] = useState(null);
  const logoutTimerRef = useRef(null);

  useEffect(() => {
    const savedToken = localStorage.getItem('accessToken');
    if (savedToken) setAccessToken(savedToken);
  }, []);

  // schedule auto-logout at JWT expiry ---
  useEffect(() => {
    // clear any previous timer
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    if (!accessToken) return;

    try {
      const [, payloadB64] = accessToken.split('.');
      if (!payloadB64) return;

      // base64url → base64
      const json = atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/'));
      const payload = JSON.parse(json);

      if (!payload.exp) return; // no exp => nothing to schedule
      const expiryMs = payload.exp * 1000;
      const delay = Math.max(0, expiryMs - Date.now());

      logoutTimerRef.current = setTimeout(() => {
        // store reason so Login can show a friendly message
        sessionStorage.setItem('logout_reason', 'expired');
        // Optional immediate notice (remove if you dislike alerts)
        // alert('Your session has expired. Please sign in again.');
        logout(); // will clear token and show Login
      }, delay + 1000); // +1 s guard for clock skew
    } catch (e) {
      // If decoding fails, do nothing; normal 401 handling will apply
      console.warn('Could not schedule auto-logout:', e);
    }

    return () => {
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    };
  }, [accessToken]);
  // -----------------------------------------------

  const logout = async () => {
    try {
      await fetch('https://dcn-backend.onrender.com/logout', {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    } catch { /* ignore */ }
    finally {
      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current);
        logoutTimerRef.current = null;
      }
      localStorage.removeItem('accessToken');
      setAccessToken(null);
    }
  };

  if (!accessToken) {
    return (
      <Login
        onLogin={(token) => {
          localStorage.setItem('accessToken', token);
          setAccessToken(token);
        }}
      />
    );
  }

  return (
    <Router>
      <Routes>
        <Route path="/" element={<MapComponent accessToken={accessToken} onLogout={logout} />} />
        <Route path="/upload" element={<CsvUploader accessToken={accessToken} onLogout={logout} />} />
        <Route path="/staging" element={<StagingInterface accessToken={accessToken} onLogout={logout} />} />
        <Route path="/staging_powerplants" element={<StagingInterfacePowerplant accessToken={accessToken} onLogout={logout} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
